import {
  createRoleComboboxOptions,
  createRoleSelectOptions,
  getRolesWithPermissions,
  mapUserRolesToValue
} from '@bo-utils/user';
import {
  useCreateRoleMutation,
  useGetRoleQuery,
  useGetRolesQuery,
  useUpdateRoleMutation
} from '@bo-services/rolesApi';
import { useMemo, useState } from 'react';
import type { ComboboxOption } from '@library/components/selects/combobox/Combobox';
import type { RolesRequest } from '@library/types/role';
import type { UserRoleAttributes } from '@library/types/user';

export interface UseRolesProps {
  roles?: Array<UserRoleAttributes>;
  rolesRequest?: RolesRequest;
  selectedRoleId?: string;
}

export const useRoles = ({
  roles = undefined,
  rolesRequest = undefined,
  selectedRoleId = ''
}: UseRolesProps) => {
  const [currentRoles, setCurrentRoles] =
    useState<Array<ComboboxOption> | null>(() =>
      roles ? mapUserRolesToValue(roles) : []
    );

  const {
    isLoading: isLoadingRoles,
    isFetching: isFetchingRoles,
    data: rolesData,
    isError: isRoleError
  } = useGetRolesQuery(rolesRequest ?? {});

  const {
    data: selectedRole,
    isLoading: isLoadingSelectedRole,
    isFetching: isFetchingSelectedRole
  } = useGetRoleQuery(
    {
      id: selectedRoleId
    },
    {
      skip: !selectedRoleId
    }
  );

  const [createRole] = useCreateRoleMutation();
  const [updateRole] = useUpdateRoleMutation();

  const { roleComboboxOptions, roleSelectOptions, rolesWithPermissions } =
    useMemo(
      () => ({
        roleComboboxOptions: createRoleComboboxOptions(rolesData),
        roleSelectOptions: createRoleSelectOptions(rolesData),
        rolesWithPermissions: getRolesWithPermissions(rolesData)
      }),
      [rolesData]
    );

  return {
    createRole,
    currentRoles,
    isRolesLoading: isLoadingRoles || isFetchingRoles,
    isRoleError,
    isSelectedRoleLoading: isLoadingSelectedRole || isFetchingSelectedRole,
    roleComboboxOptions,
    rolesData,
    rolesPagination: rolesData?.meta,
    roleSelectOptions,
    rolesWithPermissions,
    selectedRole,
    setCurrentRoles,
    updateRole
  };
};
